import { Button as AriaButton, type ButtonProps } from "react-aria-components";
import { twMerge } from "@/lib/twMerge";

const VARIANTS = {
  primary: "bg-g-orange text-g-off-black border border-g-orange font-medium hover:brightness-95 disabled:opacity-40",
  secondary: "bg-surface border border-line-mid text-text hover:bg-sunken disabled:opacity-40",
  danger: "bg-warn-bg border border-warn-line text-warn hover:brightness-95 disabled:opacity-40",
};

interface Props extends Omit<ButtonProps, "className"> {
  variant?: keyof typeof VARIANTS;
  className?: string;
}

/** The one Button in the app — every action funnels through this, so focus rings,
 * disabled state, and busy state (React Aria's `data-pending`) look consistent
 * everywhere rather than being redecided per call site. Sized to match the original
 * registry-editor.html's buttons (13px/7px-13px padding, 4px radius — sharper than
 * the 8px card radius), not an invented shape. */
export function Button({ variant = "secondary", className, ...props }: Props) {
  return (
    <AriaButton
      {...props}
      className={twMerge(
        "rounded px-3.5 py-[7px] text-[13px] transition-colors cursor-pointer",
        "data-[focus-visible]:outline data-[focus-visible]:outline-2 data-[focus-visible]:outline-accent-line",
        VARIANTS[variant],
        className,
      )}
    />
  );
}
